import { useEffect, useRef } from 'react'
import { Barcode } from 'shared/components/barcode'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { formatDateTime } from 'shared/lib/utils/date'

/**
 * A buyer's return's label (spec §22 Ф6, ADR-0037; ticket 42): the return's code as a
 * barcode and in print, its partner, its Point, «ВОЗВРАТ» and the date it was
 * accepted. Printed by the browser (`window.print`), like a Bag's label; the page's
 * print style sends only the element marked `data-print-label` to paper. `print` is
 * injected so tests see the call.
 */
export interface ReturnLabelData {
	trackingCode: string
	partnerName: string
	pointCode: string
	acceptedAt: Date | string
}

const printViaBrowser = () => window.print()

export function ReturnLabel({ label }: { label: ReturnLabelData }) {
	const { t } = useTranslate()
	return (
		<div
			data-print-label
			data-testid='return-label'
			className='flex w-[360px] flex-col gap-1.5 border-[3px] border-black bg-white p-4 font-mono text-black'
		>
			<div className='text-3xl font-extrabold tracking-widest' data-testid='return-label-word'>
				{t('returns.labelWord')}
			</div>
			<Barcode value={label.trackingCode} className='h-16 w-full' />
			<div className='text-xl font-bold' data-testid='return-label-code'>
				{label.trackingCode}
			</div>
			<div data-testid='return-label-partner'>{t('returns.labelPartner', { partner: label.partnerName })}</div>
			<div data-testid='return-label-point'>{t('returns.labelPoint', { point: label.pointCode })}</div>
			<div data-testid='return-label-date'>{t('returns.labelDate', { date: formatDateTime(label.acceptedAt) })}</div>
		</div>
	)
}

/** The label on screen with «Печать этикетки»; with `printNow` it goes to the printer once drawn. */
export function ReturnLabelPrint({
	label,
	printNow = false,
	print = printViaBrowser,
}: {
	label: ReturnLabelData
	printNow?: boolean
	print?: () => void
}) {
	const { t } = useTranslate()
	const printed = useRef(false)
	useEffect(() => {
		if (!printNow || printed.current) return
		printed.current = true
		const timer = setTimeout(print, 50)
		return () => {
			clearTimeout(timer)
			printed.current = false
		}
	}, [printNow, print])
	return (
		<div className='flex flex-col gap-2'>
			<ReturnLabel label={label} />
			<div>
				<Button type='button' variant='outline' data-testid='return-label-print' onClick={print}>
					{t('returns.labelPrint')}
				</Button>
			</div>
		</div>
	)
}
